<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
    src: { type: String, default: '' },
    poster: { type: String },
    loop: { type: Boolean, default: false },
    muted: { type: Boolean, default: true },
    playsinline: { type: Boolean, default: true },
    preload: { type: String, default: 'auto' }, // Changed from metadata for instant playback
    isAd: { type: Boolean, default: false },
    autoplay: { type: Boolean, default: false },
    orientation: { type: String, default: 'vertical' }, // vertical, horizontal, square
});

const emit = defineEmits([
    'mounted',
    'play',
    'pause',
    'ended',
    'timeupdate',
    'loadedmetadata',
    'waiting',
    'playing',
    'canplay',
    'volumechange',
    'error',
    'click',
]);

const videoEl = ref(null);

// Watch for src changes
watch(() => props.src, (newSrc) => {
    if (videoEl.value && newSrc) {
        videoEl.value.src = newSrc;
        videoEl.value.load();
    }
}, { immediate: true });

// Watch for muted changes
watch(() => props.muted, (muted) => {
    if (videoEl.value) {
        videoEl.value.muted = muted;
    }
});

// Event handlers
const handleClick = (event) => {
    emit('click', event);
};

// Setup event listeners
const setupEventListeners = () => {
    if (!videoEl.value) return;

    const events = [
        'play', 'pause', 'ended', 'timeupdate', 
        'loadedmetadata', 'waiting', 'playing', 
        'canplay', 'volumechange', 'error'
    ];

    events.forEach(event => {
        videoEl.value.addEventListener(event, (e) => emit(event, e));
    });
};

const removeEventListeners = () => {
    if (!videoEl.value) return;

    const events = [
        'play', 'pause', 'ended', 'timeupdate', 
        'loadedmetadata', 'waiting', 'playing', 
        'canplay', 'volumechange', 'error'
    ];

    events.forEach(event => {
        videoEl.value.removeEventListener(event, (e) => emit(event, e));
    });
};

// Expose video element and methods
defineExpose({
    videoEl,
    play: () => videoEl.value?.play(),
    pause: () => videoEl.value?.pause(),
    load: () => videoEl.value?.load(),
});

onMounted(() => {
    setupEventListeners();
    emit('mounted', videoEl.value);
});

onBeforeUnmount(() => {
    removeEventListeners();
});
</script>

<template>
    <video
        ref="videoEl"
        :src="src"
        :poster="poster"
        :loop="loop"
        :muted="muted"
        :playsinline="playsinline"
        :preload="preload"
        :class="[
            'w-full h-full block pointer-events-none',
            isAd ? 'object-contain' : 'object-cover md:object-contain'
        ]"
        controlslist="nodownload"
        @click="handleClick"
        @contextmenu.prevent
    >
        Your browser does not support the video tag.
    </video>
</template>